import React, { useState } from 'react';

import { styled } from 'styled-components';

import { ReactComponent as SearchIcon } from 'common/dist/assets/search.svg';

import { Button, Typography } from '@vivaahealth/design-system';
import { Col, Container, InputGroup, Modal, Row } from 'react-bootstrap';

import { api } from 'common';
import { Link, match, useHistory, useLocation } from 'react-router-dom';

import { useSelector } from 'react-redux';
import * as store from '../../../../reducers/store';

import SpecialtyProviderSearch from './SearchBar/SpecialtyProviderSearch';
import { Location } from '../Search';
import { EmbeddedFluidContainer } from './';

interface Props {
  match: match;
  border?: boolean;
  allowModal?: boolean;
  ipLocation?: api.IpStack.Response;
  locationSearch?: Location;
  setCurrentPage?: (value: number) => void;
}

const SearchBar: React.FC<Props> = ({
  match,
  border,
  allowModal,
  ipLocation,
  locationSearch,
  setCurrentPage,
}) => {
  const embedded = useSelector((store: store.All) => store.embedded);
  const searchBarBorder = border
    ? '1px solid var(--color-border-default) !important'
    : 'none';
  const [specialty, setSpecialty] = useState<string | undefined>(undefined);
  const [specialtyIconColor, setSpecialtyIconColor] = useState<string>(
    'var(--color-content-disabled)',
  );
  const [show, setShow] = useState(false);

  const { search } = useLocation();
  const history = useHistory();
  const searchQuery = new URLSearchParams(search);
  const specialtySearch = searchQuery.get('specialty');

  const handleShow = () => {
    setShow((show) => !show);
  };

  const handleSpecialtyChange = (specialty?: string) => {
    setSpecialty(specialty);
    setSpecialtyIconColor('var(--color-shade-80)');
    if (!specialty || show) return;
    history.push(getLinkPath(specialty));
  };

  const getLinkPath = (specialty?: string): object => {
    const queries: string[] = [];
    if (specialty) {
      queries.push(`specialty=${specialty}`);
    }
    return {
      pathname:
        match.url.split('/')[4] === 'search'
          ? match.url
          : `${match.url}/search`,
      search: '?' + queries.join('&'),
    };
  };

  const liteSearchBarButton = (
    <LiteSearchBarButtonContainer>
      <Col
        className={`mx-0 border border-1 rounded-1 px-3 ${
          allowModal ? 'd-md-none d-block' : 'd-none'
        }`}
      >
        <StyledInputGroup onClick={handleShow}>
          <StyledInputGroupText className="p-0" id="inputGroupPrepend">
            <StyledSearchIcon color={specialtyIconColor} />
          </StyledInputGroupText>
          <InputGroup.Text className="bg-white border-0">
            {specialtySearch ? (
              <Typography variant="paragraph-large">
                {api.Common.SpecialtyMap[specialtySearch as api.Specialty]}
              </Typography>
            ) : (
              <Typography variant="paragraph-large" className="text-muted">
                Specialty
              </Typography>
            )}
          </InputGroup.Text>
        </StyledInputGroup>
      </Col>
    </LiteSearchBarButtonContainer>
  );

  const liteSearchBarModal = (
    <Modal show={show} fullscreen={true} onHide={handleShow}>
      <StyledModalHeader className="px-4">
        <Container>
          <Row>
            <Col className="ps-0 d-flex align-items-center">
              <Button variant="text" onClick={handleShow} className="ps-0">
                Cancel
              </Button>
            </Col>
            <Col className="text-end pe-0">
              <StyledLink to={getLinkPath(specialty)}>
                <Button
                  variant="primary"
                  onClick={() => {
                    handleShow();
                    setCurrentPage?.(1);
                  }}
                >
                  Search
                </Button>
              </StyledLink>
            </Col>
          </Row>
        </Container>
      </StyledModalHeader>
      <StyledModalBody className="px-4">
        <Container>
          <Row xs={1}>
            <Col className="border border-1 rounded-1 px-0 mx-sm-0 position-relative mb-3">
              <SpecialtyProviderSearch
                specialtyIconColor={specialtyIconColor}
                initialSpecialtySearch={specialtySearch as api.Specialty}
                onSpecialtyChange={handleSpecialtyChange}
                onFocus={() => {
                  if (!specialty) {
                    setSpecialtyIconColor('var(--color-primary)');
                  }
                }}
                onBlur={() => {
                  if (!specialty) {
                    setSpecialtyIconColor('var(--color-content-disabled)');
                  }
                }}
                setCurrentPage={setCurrentPage}
              />
            </Col>
          </Row>
          <StyledHR className="position-absolute start-0" />
        </Container>
      </StyledModalBody>
    </Modal>
  );

  const content = (
    <SearchRow
      border={searchBarBorder}
      className={allowModal ? 'd-none d-md-flex mx-0' : 'mx-0'}
    >
      <Col xs={12} md={10} className="position-relative px-0">
        <SpecialtyProviderSearch
          specialtyIconColor={specialtyIconColor}
          initialSpecialtySearch={specialtySearch as api.Specialty}
          onSpecialtyChange={handleSpecialtyChange}
          onFocus={() => {
            if (!specialty) {
              setSpecialtyIconColor('var(--color-primary)');
            }
          }}
          onBlur={() => {
            if (!specialty) {
              setSpecialtyIconColor('var(--color-content-disabled)');
              history.push(getLinkPath(''));
              setCurrentPage?.(1);
            }
          }}
          setCurrentPage={setCurrentPage}
        />
      </Col>
      <MobileSearchBarDivider>
        <hr className="my-2" />
      </MobileSearchBarDivider>
      <MobileSearchBarDivider>
        <hr className="my-2" />
      </MobileSearchBarDivider>
      <Col xs={12} md={2} className="text-end my-2 pe-2">
        <StyledLink to={getLinkPath()}>
          <SearchButton
            type="submit"
            variant="primary"
            onClick={() => setCurrentPage?.(1)}
          >
            Search
          </SearchButton>
        </StyledLink>
      </Col>
    </SearchRow>
  );

  return (
    <>
      {liteSearchBarButton}
      {embedded ? (
        <EmbeddedFluidContainer fluid className="py-md-3">
          {content}
        </EmbeddedFluidContainer>
      ) : (
        <Container className="py-md-3 px-0">{content}</Container>
      )}
      {liteSearchBarModal}
    </>
  );
};

export default SearchBar;

const SearchButton = styled(Button)`
  @media (max-width: 767px) {
    width: 100%;
  }
`;

const SearchRow = styled(Row).attrs<{ border: string }>((props) => ({
  border: props.border || 'none',
}))`
  align-items: center;
  background-color: var(--color-white);
  border-radius: 4px;

  @media (min-width: 768px) {
    height: 56px;
    padding: 0;
  }

  border: ${(props) => props.border};
`;

const StyledSearchIcon = styled(SearchIcon).attrs<{ color: string }>(
  (props) => ({
    color: props.color || 'undefined',
  }),
)`
  stroke-width: 1;
  color: ${(props) => props.color};
`;

const StyledInputGroupText = styled(InputGroup.Text)`
  background-color: var(--color-white);
  color: var(--color-content-disabled);
  border: none;
`;

const StyledLink = styled(Link).attrs<{ color: string }>((props) => ({
  color: props.color || 'var(--color-black)',
}))`
  text-decoration-line: none;
`;

const StyledInputGroup = styled(InputGroup)`
  height: 48px;
  cursor: pointer;
`;

const StyledHR = styled.hr`
  height: 10px;
  width: 150%;
  color: var(--color-shade-40);
  opacity: 1;
  margin-top: 0;
`;

const StyledModalHeader = styled(Modal.Header)`
  border-color: var(--color-shade-40);
`;

const StyledModalBody = styled(Modal.Body)`
  overflow-x: hidden;
`;

const LiteSearchBarButtonContainer = styled.div`
  @media (max-width: 767px) {
    padding: 16px 0px;
  }

  @media (max-width: 576px) {
    margin: 0 1rem;
  }
`;

const MobileSearchBarDivider = styled(Col)`
  display: none;

  @media (max-width: 767px) {
    display: block;
    color: var(--color-shade-60);
  }
`;
